/**
 * Shared Analyzer API
 *
 * Usage:
 *   import { analyzeAudioFile, analyzeAudioBuffer } from './analyzer';
 *   const { bpm, bpmConfidence, key, keyConfidence } = await analyzeAudioFile(file);
 */

import { mixdownToMono, peakNormalize, resampleLinear } from './audio-utils';
import { detectBPM } from './tempo';
import { detectKey } from './key';

export interface AnalysisResult {
  bpm: number;
  bpmConfidence: number;
  key: string;
  keyConfidence: number;
}

const ANALYSIS_SAMPLE_RATE = 22050;
const MAX_SECONDS = 150; // analyze first 150s for performance

/**
 * Analyze a user-provided File object (MP3/WAV).
 */
export async function analyzeAudioFile(file: File): Promise<AnalysisResult> {
  const arrayBuffer = await file.arrayBuffer();
  const audioBuffer = await decodeAudioData(arrayBuffer);
  return analyzeAudioBuffer(audioBuffer);
}

/**
 * Analyze an already-decoded AudioBuffer.
 */
export function analyzeAudioBuffer(audioBuffer: AudioBuffer): AnalysisResult {
  const mono = mixdownToMono(audioBuffer);
  const normalized = peakNormalize(mono);

  // Trim to first MAX_SECONDS for consistent performance
  const maxSamples = Math.min(normalized.length, Math.floor(MAX_SECONDS * audioBuffer.sampleRate));
  const trimmed = normalized.subarray(0, maxSamples);

  // Resample to a predictable sample rate
  const resampled = resampleLinear(trimmed, audioBuffer.sampleRate, ANALYSIS_SAMPLE_RATE);

  const { bpm, confidence: bpmConfidence } = detectBPM(resampled, ANALYSIS_SAMPLE_RATE);
  const { key, confidence: keyConfidence } = detectKey(resampled, ANALYSIS_SAMPLE_RATE);

  return { bpm, bpmConfidence, key, keyConfidence };
}

function decodeAudioData(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  return new Promise((resolve, reject) => {
    ctx.decodeAudioData(
      arrayBuffer,
      (buffer) => {
        ctx.close();
        resolve(buffer);
      },
      (err) => {
        ctx.close();
        reject(err ?? new Error('Failed to decode audio data'));
      }
    );
  });
}
